// =============================================================================
// SIREPPAR - Validación de archivos que se suben al sistema
// =============================================================================
// El tope y su mensaje viven acá y no en cada pantalla porque hay dos caminos de
// subida: el uploader genérico `components/common/FileUpload` (caja de arrastrar,
// con preview) y los botones chicos de los wizards, que suben con URL firmada
// directo al almacenamiento. Los dos tienen que rechazar lo mismo y decirlo igual.
//
// Ojo: esto es una guarda de experiencia de uso, no de seguridad. La URL firmada
// que emite el backend no lleva condición de `Content-Length`, así que el tope real
// no está impuesto por el almacenamiento. Sirve para que a nadie se le vaya un
// escaneo enorme y la subida quede colgada sin explicación.
// =============================================================================

/** Tope de peso por archivo, en MB. Es el que usa el uploader genérico del sistema. */
export const MAX_ARCHIVO_MB = 50;

/** Extensiones que se aceptan por defecto en la documentación de trámites. */
export const ACCEPT_DOCUMENTOS = '.pdf,.jpg,.jpeg,.png';

/**
 * MIME a partir de la extensión, para cuando el navegador no lo detecta y
 * `file.type` viene vacío. Pasa con archivos sin extensión conocida en el sistema
 * de origen. Hace falta porque el backend valida el `contentType` contra una lista
 * blanca: mandar 'application/octet-stream' como comodín es un rechazo seguro.
 */
export function mimeDesdeExtension(nombre: string): string {
  const punto = nombre.lastIndexOf('.');
  const ext = punto >= 0 ? nombre.slice(punto).toLowerCase() : '';
  const porExtension: Record<string, string> = {
    '.pdf': 'application/pdf',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
  };
  return porExtension[ext] ?? 'application/octet-stream';
}

/**
 * Valida un archivo antes de subirlo. Devuelve el mensaje de error, o `null` si está
 * bien. El mensaje es el que se le muestra a la persona, así que va en español y
 * dice qué pasó y cuál es el límite.
 */
export function validarArchivo(
  file: File,
  opciones: { maxSizeMb?: number; accept?: string } = {},
): string | null {
  const maxSizeMb = opciones.maxSizeMb ?? MAX_ARCHIVO_MB;
  const accept = opciones.accept ?? ACCEPT_DOCUMENTOS;

  if (file.size > maxSizeMb * 1024 * 1024) {
    const pesa = (file.size / (1024 * 1024)).toFixed(1);
    return `El archivo pesa ${pesa}MB y el máximo es ${maxSizeMb}MB.`;
  }

  // El atributo `accept` del input es solo un filtro del diálogo del navegador: se
  // saltea eligiendo "todos los archivos", así que la extensión se revisa igual.
  const permitidas = accept.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean);
  if (permitidas.length) {
    const punto = file.name.lastIndexOf('.');
    const ext = punto >= 0 ? file.name.slice(punto).toLowerCase() : '';
    const coincide = permitidas.some((t) => (t.startsWith('.') ? t === ext : file.type === t));
    if (!coincide) {
      return `Formato no admitido. Se aceptan: ${permitidas.join(', ')}.`;
    }
  }

  return null;
}
